import React, { useState } from 'react';
import ChakraBar from './ChakraBar';
import { calculateVibeScore } from '../services/vibeScoreService';

const ChakraBarDemo: React.FC = () => {
  const [testText, setTestText] = useState('');
  const [result, setResult] = useState<any>(null);

  const analyzeText = () => {
    if (!testText.trim()) return;
    const analysis = calculateVibeScore(testText);
    setResult(analysis);
  };

  const sampleTexts = [
    {
      text: "Geweldig om te zien hoe jullie samenwerken! Dit is precies wat we nodig hebben voor groei.",
      label: "Positieve Nederlandse tekst"
    },
    {
      text: "Fantastisch... 🙄 Dit gaat echt geweldig worden.",
      label: "Sarcastische tekst"
    },
    {
      text: "5 tips voor ondernemers: 1) Focus op kwaliteit 2) Investeer in je team 3) Blijf leren",
      label: "Educatieve tekst met cijfers"
    },
    {
      text: "Dit is een crisis! Alles gaat mis en niemand doet iets. Verschrikkelijk!",
      label: "Negatieve tekst"
    },
    {
      text: "Hoe kunnen we als community beter samenwerken? Wat zijn jullie ervaringen?",
      label: "Vragen stellen"
    }
  ];

  return (
    <div className="h-full flex flex-col max-h-[calc(100vh-20rem)]">
      <div className="mb-4 flex-shrink-0">
        <h1 className="text-lg font-bold mb-1">🌈 VibeScore Frequentie Analyzer</h1>
        <p className="text-xs text-gray-600">Real-time tekst analyse met chakra visualisatie</p>
      </div>
      
      {/* Test Input - Compact */}
      <div className="mb-4 flex-shrink-0">
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-700 mb-1">Test je tekst:</label>
            <input
              type="text"
              value={testText}
              onChange={(e) => setTestText(e.target.value)}
              placeholder="Type hier je tekst..."
              className="w-full p-2 border rounded text-xs"
              onKeyPress={(e) => e.key === 'Enter' && analyzeText()}
            />
          </div>
          <button
            onClick={analyzeText}
            className="px-3 py-2 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
          >
            Analyseer
          </button>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400">
        {/* Frequency Analyzer Grid */}
        <div className="mb-4">
          <h2 className="text-sm font-semibold mb-2">📊 Frequentie Spectrum</h2>
          <div className="grid grid-cols-1 gap-2">
            {sampleTexts.map((sample, index) => {
              const analysis = calculateVibeScore(sample.text);
              const chakraColor = getChakraColorFromScore(analysis.score);
              
              return (
                <div key={index} className="bg-white border rounded p-3 hover:shadow-sm transition-shadow">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <div 
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: chakraColor.color }}
                      />
                      <div>
                        <h3 className="font-medium text-xs text-gray-900">{sample.label}</h3>
                        <p className="text-xs text-gray-500 max-w-xs truncate">"{sample.text}"</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold" style={{ color: chakraColor.color }}>
                        {analysis.score}
                      </div>
                      <div className="text-xs text-gray-500">{chakraColor.name}</div>
                    </div>
                  </div>
                  
                  {/* Compact Frequency Bar */}
                  <div className="relative">
                    <div 
                      className="h-1.5 rounded overflow-hidden"
                      style={{
                        background: 'linear-gradient(90deg, #E53935 0%, #FB8C00 14.3%, #FDD835 28.6%, #43A047 42.9%, #1E88E5 57.2%, #3949AB 71.5%, #8E24AA 85.8%, #8E24AA 100%)'
                      }}
                    >
                      <div
                        className="absolute right-0 top-0 h-full bg-slate-200 opacity-50"
                        style={{ width: `${100 - analysis.score}%` }}
                      />
                      <div
                        className="absolute top-0 w-0.5 h-full bg-white shadow-sm"
                        style={{ left: `${Math.min(analysis.score, 98)}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-gray-400 mt-1">
                      <span>0</span>
                      <span>25</span>
                      <span>50</span>
                      <span>75</span>
                      <span>100</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Custom Result - Compact */}
        {result && (
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded p-3">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-gray-900">🎯 Jouw Analyse</h2>
              <div className="text-right">
                <div className="text-lg font-bold" style={{ color: result.chakraColor.color }}>
                  {result.score}/100
                </div>
                <div className="text-xs text-gray-600">{result.chakraColor.name}</div>
              </div>
            </div>
            
            <div className="mb-2">
              <p className="text-xs text-gray-700 bg-white p-2 rounded border">"{testText}"</p>
            </div>
            
            <ChakraBar 
              score={result.score} 
              breakdown={result.breakdown}
              showDetails={false}
            />
            
            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
              <div className="text-center">
                <div className="text-green-600 font-semibold">+{result.breakdown.positiveScore.toFixed(1)}</div>
                <div className="text-gray-500">Positief</div>
              </div>
              <div className="text-center">
                <div className="text-red-600 font-semibold">-{result.breakdown.negativeScore.toFixed(1)}</div>
                <div className="text-gray-500">Negatief</div>
              </div>
              <div className="text-center">
                <div className="text-blue-600 font-semibold">{result.breakdown.language === 'nl' ? 'NL' : 'EN'}</div>
                <div className="text-gray-500">Taal</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Get chakra color information from score
 */
function getChakraColorFromScore(score: number) {
  if (score >= 90) return { color: '#8E24AA', name: 'kroon', description: 'Wijs, voedzaam' };
  if (score >= 75) return { color: '#3949AB', name: 'derde oog', description: 'Inzicht' };
  if (score >= 60) return { color: '#1E88E5', name: 'keel', description: 'Heldere communicatie' };
  if (score >= 45) return { color: '#43A047', name: 'hart', description: 'Hart, verbinding' };
  if (score >= 30) return { color: '#FDD835', name: 'zonnevlecht', description: 'Drive, richting' };
  if (score >= 15) return { color: '#FB8C00', name: 'sacraal', description: 'Creatie, maar onrustig' };
  return { color: '#E53935', name: 'wortel', description: 'Root, mogelijk triggerend' };
}

export default ChakraBarDemo;

